import * as auth from './auth.js';
import * as i18n from './i18n.js';
import { t, currentLocale } from './i18n.js';
import { setState } from './store.js';
import { apiGet, setLocaleProvider, setToastProvider, setTranslateProvider, setOnRefreshSuccess } from './api.js';
import * as router from './router.js';
import { mountAuthedShell } from './shell.js';
import { toast } from './components/toast.js';
import { registerServiceWorker } from './components/sw-register.js';
import { prefetchWhenIdle } from './lazy-module.js';

// Login is the only page imported statically. It is one of the two possible
// landing screens and the only one an unauthenticated cold start can reach, so
// putting it behind a chunk fetch would add a round trip to the very first paint
// a new user sees — to save 2.8 KB. Every other page is lazy; see below.
import { route as loginRoute } from './pages/login.js';

// Pages, one chunk each. `lazy()` owns the loading and failure states — see its
// doc comment in router.js for why a rejected import must not be fatal.
//
// The whole point is that this list contains no static page imports: a single
// one would pull that page, and everything it imports, back into the entry
// bundle. sortablejs (35 KB, three admin screens) and the scanner (17 KB) are
// the two clearest cases — they were previously downloaded by everyone on every
// cold start, including on the inventory list, which uses neither.
const inventoryRoute          = router.lazy(() => import('./pages/inventory.js'));
const quickAddRoute           = router.lazy(() => import('./pages/quick-add.js'));
const catalogRoute            = router.lazy(() => import('./pages/catalog.js'));
const locationsRoute          = router.lazy(() => import('./pages/locations.js'));
const historyRoute            = router.lazy(() => import('./pages/history.js'));
const historyItemRoute        = router.lazy(() => import('./pages/history.js'), (m) => m.itemRoute);
const unitsRoute              = router.lazy(() => import('./pages/units.js'));
const usersRoute              = router.lazy(() => import('./pages/users.js'));
const profileRoute            = router.lazy(() => import('./pages/profile.js'));
const remindersRoute          = router.lazy(() => import('./pages/reminders.js'));
const productMastersRoute     = router.lazy(() => import('./pages/product-masters.js'), (m) => m.productMastersRoute);
const recipesRoute            = router.lazy(() => import('./pages/recipes.js'));
const recipeDetailRoute       = router.lazy(() => import('./pages/recipe-detail.js'), (m) => m.detailRoute);
const recipeCategoriesRoute   = router.lazy(() => import('./pages/recipe-categories.js'));
const ingredientsRoute        = router.lazy(() => import('./pages/ingredients.js'));
const ingredientMappingRoute  = router.lazy(() => import('./pages/ingredient-mapping.js'));
const shoppingRoute           = router.lazy(() => import('./pages/shopping.js'));
const shoppingPutAwayRoute    = router.lazy(() => import('./pages/shopping-put-away.js'));
const shoppingCategoriesRoute = router.lazy(() => import('./pages/shopping-categories.js'));
const timersRoute             = router.lazy(() => import('./pages/timers.js'));

// The four rows the bottom bar projects (CATALOG_ITEMS `primary: true`). These
// are the navigations a user makes within seconds of the app opening, so their
// chunks are warmed once the landing route has settled — after first render, at
// idle, so the prefetch never competes with the bootstrap requests above it.
const PREFETCH_ROUTES = [inventoryRoute, shoppingRoute, quickAddRoute, catalogRoute];

/**
 * Warm the primary tab chunks in the background.
 *
 * The idle scheduling and the metered-connection opt-out live in
 * `prefetchWhenIdle` and are shared with the interaction-level gates on the
 * pages — one "should we prefetch?" rule, not one per caller.
 */
function prefetchPrimaryRoutes() {
  prefetchWhenIdle(() => { for (const r of PREFETCH_ROUTES) r.preload(); });
}

function pickInitialLocale() {
  const saved = localStorage.getItem('lardr.locale');
  if (saved) return saved;
  const nav = (navigator.language || 'en').toLowerCase();
  if (nav.startsWith('de')) return 'de';
  return 'en';
}

function applyInitialTheme() {
  const saved = localStorage.getItem('lardr.theme');
  const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  if (saved === 'dark' || (saved == null && prefersDark)) {
    document.documentElement.classList.add('dark');
  }
}

function bindOfflineBanner() {
  const banner = document.createElement('div');
  banner.id = 'offline-banner';
  // Offset below the sticky header and to the right of the sidebar at `lg`.
  // Anchored at top-0 it covered the header outright — brand, bell, timer and
  // profile all became unreachable the moment the connection dropped.
  banner.className = 'hidden fixed top-[64px] lg:left-[244px] inset-x-0 z-50 bg-warning text-white text-center text-sm py-2';
  banner.textContent = t('errors.offline_banner');
  document.body.prepend(banner);
  const update = () => {
    if (navigator.onLine) banner.classList.add('hidden');
    else banner.classList.remove('hidden');
  };
  window.addEventListener('online', update);
  window.addEventListener('offline', update);
  update();
}

async function main() {
  applyInitialTheme();

  // Forward currentLocale() on every request so the backend can serve
  // localized fields (quick-add labels, inventory product_name).
  setLocaleProvider(() => currentLocale() || null);

  // Wire toast and i18n hooks so api.js can show toasts without importing
  // toast.js or i18n.js directly (both have transitive deps on api.js).
  setToastProvider((msg, kind) => toast.show(msg, kind));
  setTranslateProvider(t);

  // After a silent refresh, write the new token back to the correct storage
  // (same remember-me setting as the original login).
  setOnRefreshSuccess((newTok) => {
    const remember = localStorage.getItem('lardr.token') !== null;
    auth.set(newTok, remember);
  });

  // Rehydrate user from localStorage
  const u = auth.currentUser();
  if (u) setState({ user: u });

  // Load locale bundle
  const locale = pickInitialLocale();
  try {
    await i18n.loadLocale(locale);
  } catch {
    // continue with empty dict — UI shows raw keys
  }

  // Offline banner — placed after locale load so t() has the bundle
  bindOfflineBanner();

  // Fetch unit catalogue (unauthenticated). Must run after locale load so
  // the unit-picker helper can call t('units.*') immediately.
  try {
    const units = await apiGet('/units');
    setState({ units });
  } catch {
    // Leave units: null — unit-select.js falls back to a free-text input.
  }

  // Fetch SPA bootstrap config (§11.4.1) — currently `ocr_adapter`. Scanner
  // uses this to flip status text on the cloud-processing wait. Failure is
  // non-fatal: scanner falls back to the default progress text.
  try {
    const config = await apiGet('/config');
    setState({ config });
  } catch {
    // Leave config: null — scanner.js treats null adapter as self-hosted-fast.
  }

  // Register routes
  router.register('/login',     { ...loginRoute,     public: true });
  router.register('/',          inventoryRoute);
  router.register('/quick-add', quickAddRoute);
  router.register('/catalog',   catalogRoute);
  router.register('/locations', locationsRoute);
  router.register('/history',   historyRoute);
  router.register('/history/:itemId', historyItemRoute);
  router.register('/units',     unitsRoute);
  router.register('/users',     usersRoute);
  router.register('/profile',   profileRoute);
  router.register('/reminders', remindersRoute);
  router.register('/product-masters', productMastersRoute);
  router.register('/recipes',             recipesRoute);
  router.register('/recipes/:id',         recipeDetailRoute);
  router.register('/recipe-categories',   recipeCategoriesRoute);
  router.register('/ingredients',         ingredientsRoute);
  router.register('/ingredient-mapping',  ingredientMappingRoute);
  router.register('/shopping',            shoppingRoute);
  router.register('/shopping/put-away',   shoppingPutAwayRoute);
  router.register('/shopping-categories', shoppingCategoriesRoute);
  router.register('/timers',              timersRoute);

  // Prime household payload (when authed) and mount the shell. Shared with
  // post-login and post-logout flows so the layout reflects auth changes.
  await mountAuthedShell();
  router.start();

  // Only for a signed-in session: on the login screen these four chunks are all
  // unreachable, and three of them would 401 the moment they tried to load data.
  if (auth.isAuthenticated()) prefetchPrimaryRoutes();

  // Register the service worker. main() has already awaited bootstrap and
  // mounted the shell above, so first paint has settled — we register directly.
  // (Gating on the `load` event here was a bug: `load` fires long before these
  // awaits resolve, so the listener never ran and the SW never registered.)
  registerServiceWorker();
}

main();
